/**
 * Copyright (c) Siemens 2016 - 2026
 * SPDX-License-Identifier: MIT
 */
import { A11yModule } from '@angular/cdk/a11y';
import { booleanAttribute, Component, computed, inject, input, output } from '@angular/core';
import { elementCancel, elementDown2 } from '@siemens/element-icons';
import { addIcons, SiIconComponent } from '@siemens/element-ng/icon';
import { SiLinkModule } from '@siemens/element-ng/link';
import { SiTranslatePipe, t, TranslatableString } from '@siemens/element-translate-ng/translate';

import { SiApplicationHeaderComponent } from '../si-application-header.component';
import { SiLaunchpadCategoryComponent } from './si-launchpad-category.component';
import { App, AppCategory, FavoriteChangeEvent } from './si-launchpad.model';

@Component({
  selector: 'si-launchpad-factory',
  imports: [
    A11yModule,
    SiLinkModule,
    SiTranslatePipe,
    SiLaunchpadCategoryComponent,
    SiIconComponent
  ],
  templateUrl: './si-launchpad-factory.component.html',
  styleUrl: './si-launchpad-factory.component.scss'
})
export class SiLaunchpadFactoryComponent {
  /**
   * Text to close the launchpad. Needed for a11y.
   *
   * @defaultValue
   * ```
   * t(() => $localize`:@@SI_LAUNCHPAD.CLOSE:Close launchpad`)
   * ```
   */
  readonly closeText = input(t(() => $localize`:@@SI_LAUNCHPAD.CLOSE:Close launchpad`));

  /**
   * Title of the launchpad.
   *
   * @defaultValue
   * ```
   * t(() => $localize`:@@SI_LAUNCHPAD.TITLE:Switch app`)
   * ```
   */
  readonly titleText = input(t(() => $localize`:@@SI_LAUNCHPAD.TITLE:Switch app`));

  /**
   * Subtitle of the launchpad.
   * When not provided, no subtitle is displayed.
   */
  readonly subtitleText = input<TranslatableString>();

  /** All app items shown in the launchpad. */
  readonly apps = input.required<App[] | AppCategory[]>();

  /**
   * Allow the user to select favorite apps which will then be displayed at the top.
   *
   * @defaultValue false
   */
  readonly enableFavorites = input(false, { transform: booleanAttribute });

  /**
   * Title of the favorite apps section.
   *
   * @defaultValue
   * ```
   * t(() => $localize`:@@SI_LAUNCHPAD.FAVORITE_APPS:Favorites`)
   * ```
   */
  readonly favoriteAppsText = input(t(() => $localize`:@@SI_LAUNCHPAD.FAVORITE_APPS:Favorites`));

  /**
   * Title of the show more apps button.
   *
   * @defaultValue
   * ```
   * t(() => $localize`:@@SI_LAUNCHPAD.SHOW_MORE:Show more`)
   * ```
   */
  readonly showMoreAppsText = input(t(() => $localize`:@@SI_LAUNCHPAD.SHOW_MORE:Show more`));

  /**
   * Title of the show less apps button.
   *
   * @defaultValue
   * ```
   * t(() => $localize`:@@SI_LAUNCHPAD.SHOW_LESS:Show less`)
   * ```
   */
  readonly showLessAppsText = input(t(() => $localize`:@@SI_LAUNCHPAD.SHOW_LESS:Show less`));

  readonly favoriteChange = output<FavoriteChangeEvent>();

  protected showAllApps = false;
  protected readonly categories = computed(() => {
    const apps = this.apps();
    const favorites = this.favorites();
    const categories: AppCategory[] = [];
    if (this.enableFavorites() && this.hasFavorites()) {
      categories.push({
        name: this.favoriteAppsText(),
        apps: favorites
      });
    }

    if (this.isCategories(apps)) {
      categories.push(...apps);
    } else {
      categories.push({ name: '', apps: [...apps] });
    }
    return categories;
  });
  protected readonly favorites = computed(() =>
    this.apps()
      .flatMap(app => ('apps' in app ? app.apps : (app as App)))
      .filter(app => app.favorite)
  );
  protected readonly hasFavorites = computed(() => this.favorites().length > 0);
  protected readonly icons = addIcons({ elementDown2, elementCancel });
  private header = inject(SiApplicationHeaderComponent);

  protected closeLaunchpad(): void {
    this.header.closeLaunchpad();
  }

  protected toggleFavorite(event: FavoriteChangeEvent): void {
    this.favoriteChange.emit(event);
  }

  protected escape(): void {
    this.closeLaunchpad();
  }

  protected isCategories(items: App[] | AppCategory[]): items is AppCategory[] {
    return items.some(item => 'apps' in item);
  }
}
